import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import TopNav from '../components/sections/TopNav';
import SiteFooter from '../components/sections/SiteFooter';
import AuthSuccessSplash from '../components/AuthSuccessSplash';
import { useAuth } from '../context/AuthContext';

/**
 * Magic-link landing.
 *   1. Reads `?token=` from the URL.
 *   2. Swaps it for a session cookie via AuthContext.
 *   3. On success renders the shared `AuthSuccessSplash` for ~2s so the
 *      reader visibly sees the auth succeeded, then routes them to
 *      /account (if they already have a plan) or /subscribe (if they don't
 *      - unifies with the Google flow so newly-signed-in visitors always
 *      land on the pricing grid).
 */
const AuthVerify = () => {
  const [params] = useSearchParams();
  const { verifyMagicToken, user } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying | ok | error
  const [verifiedEmail, setVerifiedEmail] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const token = params.get('token');
    if (!token) {
      setStatus('error');
      setError('Missing login token.');
      return;
    }
    verifyMagicToken(token)
      .then((res) => {
        setVerifiedEmail(res?.email || '');
        setStatus('ok');
      })
      .catch((err) => {
        setStatus('error');
        setError(err.message || 'That link expired or was already used.');
      });
    // Intentionally run once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (status === 'ok') {
    return (
      <AuthSuccessSplash
        email={verifiedEmail}
        subline="Taking you to your pricing plans…"
        onDone={() => {
          // Route by plan state at the moment of redirect. Paid users go
          // straight to /account, unpaid users to /subscribe.
          const dest = user && user.plan && user.plan !== 'free' ? '/account?welcome=1' : '/subscribe?welcome=1';
          navigate(dest, { replace: true });
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="auth-verify-page">
      <TopNav />
      <main className="max-w-md mx-auto px-4 py-16 text-center">
        {status === 'verifying' && (
          <>
            <div className="inline-block h-6 w-6 rounded-full border-2 border-emerald-500 border-t-transparent animate-spin mb-3" />
            <p className="text-zinc-400 text-sm uppercase tracking-[0.3em]">Verifying your link…</p>
          </>
        )}
        {status === 'error' && (
          <>
            <p className="text-red-500 text-[11px] font-black uppercase tracking-[0.3em] mb-2">Trouble</p>
            <h1 className="text-2xl font-black mb-3">{error}</h1>
            <Link to="/login" data-testid="auth-verify-retry" className="text-red-400 underline text-sm">Request a fresh link</Link>
          </>
        )}
      </main>
      <SiteFooter />
    </div>
  );
};

export default AuthVerify;
